import { FetchSSEOptions, SSEEvent } from './type';
interface RequestParams {
  url: string;
  data: any;
  success: (res: any) => void;
  fail?: () => void;
  complete?: (isOk: Boolean, msg?: String, requestid?: String) => void;
  cancel?: (res: any) => void;
  abort?: () => void;
}
/**
 * Sends a Server-Sent Events (SSE) request to the specified URL with the given data and callback function.
 * @param url - The URL to send the SSE request to.
 * @param data - The data to send with the SSE request.
 * @param callback - The function to call when the SSE response is received.
 * @returns void
 */
export async function sseRequest(params: RequestParams, apiKey?: string) {
  const { url, data, complete, cancel, abort } = params;
  const controller = new AbortController();
  const { signal } = controller;
  cancel?.(controller);
  const tokenHeaders: any = {
    'Content-Type': 'application/json',
  };
  tokenHeaders.Authorization = `Bearer${apiKey ? ` ${apiKey}` : ''}`;
  const responsePromise = fetch(url, {
    method: 'POST',
    headers: tokenHeaders,
    body: JSON.stringify(data),
    signal: controller.signal,
  }).catch((e) => {
    const msg = e.toString() || '流式接口异常';
    complete?.(false, msg);
    return Promise.reject(e); // 确保错误能够被后续的.catch()捕获
  });
  fetchSSE(() => responsePromise, params);
  signal.addEventListener('abort', () => {
    try {
      abort && abort();
    } catch {}
  });
}

// 解析SSE数据
export const fetchSSE = async (fetchFn: () => Promise<Response>, options: FetchSSEOptions = {}) => {
  const { success, fail, complete } = options;
  const responsePromise = fetchFn().catch((e) => {
    const msg = e.toString() || '流式接口异常';
    complete?.(false, msg);
    return Promise.reject(e); // 确保错误能够被后续的.catch()捕获
  });

  responsePromise
    .then((response) => {
      if (!response?.ok) {
        complete?.(false, response.statusText);
        fail?.();
        throw new Error('Request failed'); // 抛出错误以便链式调用中的下一个.catch()处理
      }
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      if (!reader) throw new Error('No reader available');

      const bufferArr: string[] = [];
      let dataText = ''; // 记录数据
      const event: SSEEvent = { type: null, data: null };

      async function processText({ done, value }: ReadableStreamReadResult<Uint8Array>): Promise<void> {
        if (done) {
          complete?.(true);
          return Promise.resolve();
        }
        const chunk = decoder.decode(value);
        const buffers = chunk.toString().split(/\r?\n/);
        bufferArr.push(...buffers);
        const i = 0;
        while (i < bufferArr.length) {
          const line = bufferArr[i];
          if (line) {
            dataText += line;
            const response = line.slice(6);
            if (response === '[DONE]') {
              event.type = 'finish';
              dataText = '';
            } else {
              const choices = JSON.parse(response.trim())?.choices?.[0];
              if (choices.finish_reason === 'stop') {
                event.type = 'finish';
                dataText = '';
              } else {
                event.type = 'delta';
                event.data = choices;
              }
            }
          }
          if (event.type && event.data) {
            const jsonData = JSON.parse(JSON.stringify(event));
            success(jsonData);
            event.type = null;
            event.data = null;
          }
          bufferArr.splice(i, 1);
        }
        return reader.read().then(processText);
      }

      return reader.read().then(processText);
    })
    .catch(() => {
      // 处理整个链式调用过程中发生的任何错误
      fail?.();
    });
};
